import { useEffect, useRef, useState } from 'react';
import type {
  RealtimeStatus,
  SyncStatus,
} from '@/hooks/use-conversation-channel';
import { useTabVisibility } from '@/hooks/use-tab-visibility';

interface RealtimeStatusBadgeProps {
  status: RealtimeStatus;
  /**
   * Phase 1.5 polish — when the WS is `connected` but the REST `/resync` is
   * in cooldown (cold-start rate-limit window), surface a subtle "Syncing…"
   * indicator. Renders only when `status === 'connected' && syncStatus === 'syncing'`
   * so the WS-state badges (reconnecting/polling/offline) keep priority.
   */
  syncStatus?: SyncStatus;
}

const STATUS_CONFIG: Record<
  Exclude<RealtimeStatus, 'connected'>,
  { color: string; copy: string; tooltip: string }
> = {
  reconnecting: {
    color: 'text-warn',
    copy: 'Reconnecting…',
    tooltip: 'Live connection dropped — retrying. Edits still save normally.',
  },
  polling: {
    color: 'text-text-muted',
    copy: 'Polling (live updates lagged)',
    tooltip:
      'Live WebSocket dropped — falling back to 5s polling. Edits still save normally.',
  },
  offline: {
    color: 'text-danger',
    copy: 'Offline — updates paused',
    tooltip:
      'No connection to the live channel. Edits made now will sync when connection is restored.',
  },
};

const SYNCING_CONFIG = {
  color: 'text-text-muted',
  copy: 'Syncing…',
  tooltip: 'Catching up on recent changes — your edits will save normally.',
} as const;

/**
 * Debounce thresholds — 2026-05-27 anti-flash hardening.
 *
 * - `SHOW_DELAY_MS` (300ms): only show non-connected chrome after the bad
 *   state persists for this long. A WS that drops + reconnects in <300ms
 *   never produces a visible badge — kills the "flashing Reconnecting…"
 *   symptom users see when the tab regains focus and the WS heartbeat
 *   reconciles in a few hundred ms.
 * - `MIN_VISIBLE_MS` (1000ms): once we've committed to showing chrome, hold
 *   it for at least this long so the badge can't strobe off-on-off in tight
 *   reconnect blips mid-recovery.
 * - `SYNC_SHOW_DELAY_MS` (500ms): "Syncing…" specifically — the happy-path
 *   `/resync` finishes in <200ms, so we shouldn't paint chrome for it at
 *   all on the normal path. Only true rate-limited backoffs (which run for
 *   >1s) cross this threshold.
 */
const SHOW_DELAY_MS = 300;
const MIN_VISIBLE_MS = 1000;
const SYNC_SHOW_DELAY_MS = 500;

type ResolvedKey = 'reconnecting' | 'polling' | 'offline' | 'syncing' | null;

function resolveKey(
  status: RealtimeStatus,
  syncStatus: SyncStatus | undefined
): ResolvedKey {
  if (status !== 'connected') return status;
  if (syncStatus === 'syncing') return 'syncing';
  return null;
}

/**
 * Returns the debounced key the badge SHOULD render, or `null` for "render
 * nothing". Hides while the tab is not visible (no point painting chrome
 * the user can't see). Imposes show-delay before showing, min-visible
 * duration once shown.
 */
function useDebouncedBadgeKey(
  rawKey: ResolvedKey,
  isVisible: boolean
): ResolvedKey {
  const [visibleKey, setVisibleKey] = useState<ResolvedKey>(null);
  const visibleSinceRef = useRef<number | null>(null);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const clearTimers = (): void => {
      if (showTimerRef.current != null) {
        window.clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      if (hideTimerRef.current != null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    // Tab hidden → drop chrome immediately. Saves the redundant flash that
    // would otherwise paint on every backgrounded tab during a global WS
    // hiccup.
    if (!isVisible) {
      clearTimers();
      setVisibleKey(null);
      visibleSinceRef.current = null;
      return;
    }

    // No chrome wanted: gate by MIN_VISIBLE_MS so we don't strobe.
    if (rawKey == null) {
      if (visibleKey == null) {
        clearTimers();
        return;
      }
      const visibleSince = visibleSinceRef.current ?? Date.now();
      const elapsed = Date.now() - visibleSince;
      const remaining = MIN_VISIBLE_MS - elapsed;
      if (remaining <= 0) {
        clearTimers();
        setVisibleKey(null);
        visibleSinceRef.current = null;
      } else {
        if (hideTimerRef.current != null)
          window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = window.setTimeout(() => {
          setVisibleKey(null);
          visibleSinceRef.current = null;
          hideTimerRef.current = null;
        }, remaining);
      }
      // Cancel any pending show; we no longer want chrome.
      if (showTimerRef.current != null) {
        window.clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      return;
    }

    // Chrome wanted. If already showing this key, no-op. If showing a
    // different key, swap immediately AND reset the visibleSince clock so
    // MIN_VISIBLE_MS applies to the new key.
    if (visibleKey === rawKey) {
      // Cancel any pending hide — we still want chrome.
      if (hideTimerRef.current != null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      return;
    }
    if (visibleKey != null && visibleKey !== rawKey) {
      clearTimers();
      setVisibleKey(rawKey);
      visibleSinceRef.current = Date.now();
      return;
    }

    // Not currently showing chrome — debounce the show.
    const delay = rawKey === 'syncing' ? SYNC_SHOW_DELAY_MS : SHOW_DELAY_MS;
    if (showTimerRef.current != null) window.clearTimeout(showTimerRef.current);
    showTimerRef.current = window.setTimeout(() => {
      setVisibleKey(rawKey);
      visibleSinceRef.current = Date.now();
      showTimerRef.current = null;
    }, delay);
  }, [rawKey, isVisible, visibleKey]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (showTimerRef.current != null)
        window.clearTimeout(showTimerRef.current);
      if (hideTimerRef.current != null)
        window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  return visibleKey;
}

export function RealtimeStatusBadge({
  status,
  syncStatus,
}: RealtimeStatusBadgeProps) {
  const { isVisible } = useTabVisibility();
  const rawKey = resolveKey(status, syncStatus);
  const key = useDebouncedBadgeKey(rawKey, isVisible);

  if (key == null) return null;

  if (key === 'syncing') {
    return (
      <div
        className={`flex items-center gap-1.5 ${SYNCING_CONFIG.color} text-micro font-mono tracking-[0.5px] shrink-0`}
        title={SYNCING_CONFIG.tooltip}
        data-testid="realtime-status-badge"
        data-status="syncing"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
        <span>{SYNCING_CONFIG.copy}</span>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[key];
  return (
    <div
      className={`flex items-center gap-1.5 ${cfg.color} text-micro font-mono tracking-[0.5px] shrink-0`}
      title={cfg.tooltip}
      data-testid="realtime-status-badge"
      data-status={key}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
      <span>{cfg.copy}</span>
    </div>
  );
}
